import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { useAppDispatch } from '@/hooks/useAppDispatch';
import { signMultiAppAndFill } from '@/slices/authSlice';
import { MULTI_APP_PARAM } from '@/solaris-lens-common-lib-web/src/constants/multiApp';
import { runMultiAppReceiveHandoff } from '@/solaris-lens-common-lib-web/src/utils/runMultiAppReceiveHandoff';
import { store } from '@/store';
import { getPostLoginPath } from '@/utils/getPostLoginPath';

/** Captured once at module load so Strict Mode remounts still see the original `?multiApp=`. */
const PAGE_LOAD_SEARCH =
  typeof window !== 'undefined' ? window.location.search : '';
const PAGE_LOAD_HAS_MULTI_APP = Boolean(
  new URLSearchParams(PAGE_LOAD_SEARCH).get(MULTI_APP_PARAM)
);

/**
 * Handoff must run and navigate exactly once per page load.
 * Do not put `navigate` in effect deps — RR7 can change its identity on
 * location updates and would re-force the landing path, blocking in-app nav.
 */
let bootPromise: Promise<string | null> | null = null;
let bootLandingPath: string | null | undefined;

const runMultiAppBoot = (
  exchangeSession: (
    multiApp: string
  ) => Promise<{ accessToken?: string; username?: string } | null>
) => {
  if (!bootPromise) {
    bootPromise = (async () => {
      try {
        const exchanged = await runMultiAppReceiveHandoff({
          search: PAGE_LOAD_SEARCH,
          exchangeSession
        });

        const auth = store.getState().auth;
        const signedIn = Boolean(auth?.accessToken && auth?.username);
        const permissions =
          store.getState().auditableElements?.authorizations?.permissions || {};

        bootLandingPath = getPostLoginPath(permissions, {
          signedIn: Boolean(exchanged && signedIn)
        });
        return bootLandingPath;
      } catch {
        bootLandingPath = '/login';
        return bootLandingPath;
      }
    })();
  }

  return bootPromise;
};

/**
 * Processes `?multiApp=` once at boot via commons `runMultiAppReceiveHandoff`.
 * Returns false while handoff runs so App can defer token refresh.
 */
export const useMultiAppAutologin = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(() => !PAGE_LOAD_HAS_MULTI_APP);
  const [landingPath, setLandingPath] = useState<string | null>(null);

  useEffect(() => {
    if (!PAGE_LOAD_HAS_MULTI_APP) {
      setIsReady(true);
      return undefined;
    }

    // Already finished (e.g. Strict Mode remount after navigate was applied).
    if (bootLandingPath !== undefined) {
      setIsReady(true);
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      const nextPath = await runMultiAppBoot(multiApp =>
        dispatch(signMultiAppAndFill(multiApp))
      );

      if (cancelled) {
        return;
      }

      // Mount routes first, then apply landing navigation once.
      setIsReady(true);
      setLandingPath(nextPath);
    };

    run();

    return () => {
      cancelled = true;
    };
    // Intentionally only `dispatch` — never re-run on `navigate` identity changes.
  }, [dispatch]);

  useEffect(() => {
    if (!landingPath) {
      return undefined;
    }

    navigate(landingPath, { replace: true });
    setLandingPath(null);
    return undefined;
  }, [landingPath, navigate]);

  return isReady;
};
